import { useCallback, useEffect, useState } from "react";
import { tr } from "@/i18n";
import { ChevronDown, ChevronRight, RefreshCw, ScrollText, Search, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAuditLog, getAuditLogFacets } from "@/lib/api";
import { SettingsPanel, PanelHeader, EmptyState } from "@/components/settings/SettingsUI";

const ALL = "__all__";
const PAGE = 50;

const fmtAt = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const CATEGORY_STYLE = {
  tax_override: "bg-amber-100 text-amber-800 border-amber-300",
  request: "bg-slate-100 text-slate-600 border-slate-200",
};

const METHOD_STYLE = { POST: "text-emerald-700", PUT: "text-blue-700", PATCH: "text-blue-700", DELETE: "text-red-700" };

function AuditRow({ row }) {
  const [open, setOpen] = useState(false);
  const hasDetails = row.details && Object.keys(row.details).length > 0;
  const failed = row.status_code && row.status_code >= 400;
  return (
    <>
      <tr className={`border-t border-slate-100 hover:bg-slate-50 ${hasDetails ? "cursor-pointer" : ""}`} onClick={() => hasDetails && setOpen(o => !o)} data-testid={`audit-row-${row.id}`}>
        <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap tabular-nums">{fmtAt(row.at)}</td>
        <td className="px-3 py-2 text-xs">
          <div className="font-medium text-slate-800">{row.user_name || "—"}</div>
          <div className="text-[10px] text-slate-400">{row.user_role || ""}</div>
        </td>
        <td className="px-3 py-2 text-xs">
          <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_STYLE[row.category] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
            {row.category === "tax_override" ? "tax override" : (row.entity_type || "").replace(/-/g, " ")}
          </span>
        </td>
        <td className="px-3 py-2 text-xs text-slate-700">
          <div className="flex items-start gap-1">
            {hasDetails && (open ? <ChevronDown className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />)}
            <span className={failed ? "text-red-700" : ""}>{row.summary}</span>
          </div>
          {row.path && (
            <div className="text-[10px] text-slate-400 font-mono mt-0.5">
              <span className={METHOD_STYLE[row.method] || ""}>{row.method}</span> {row.path}
              {row.status_code ? <span className={failed ? "text-red-600" : ""}> → {row.status_code}</span> : null}
            </div>
          )}
        </td>
      </tr>
      {open && hasDetails && (
        <tr className="bg-amber-50/40" data-testid={`audit-row-details-${row.id}`}>
          <td colSpan={4} className="px-6 py-2">
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-[11px]">
              {Object.entries(row.details).filter(([, v]) => v !== null && v !== undefined && v !== "" && typeof v !== "object").map(([k, v]) => (
                <div key={k} className="flex flex-col">
                  <dt className="text-slate-400 uppercase tracking-wide text-[9px]">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-slate-800 tabular-nums">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AuditLogPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState({ users: [], entity_types: [], categories: [] });
  const [q, setQ] = useState("");
  const [userId, setUserId] = useState(ALL);
  const [entityType, setEntityType] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE };
      if (q.trim()) params.q = q.trim();
      if (userId !== ALL) params.user_id = userId;
      if (entityType !== ALL) params.entity_type = entityType;
      if (category !== ALL) params.category = category;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await getAuditLog(params);
      setRows(res.data.items || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, q, userId, entityType, category, dateFrom, dateTo]);

  useEffect(() => { getAuditLogFacets().then(r => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => load(page), 250); return () => clearTimeout(t); }, [load, page]);
  useEffect(() => { setPage(1); }, [q, userId, entityType, category, dateFrom, dateTo]);

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <SettingsPanel>
      <PanelHeader
        icon={ScrollText}
        title={tr("Audit Log")}
        hint="Every change made in the app — who did what, when, and to which record. Read-only."
        actions={<Button size="sm" variant="outline" onClick={() => load(page)} disabled={loading} data-testid="audit-refresh-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> {tr("Refresh")}</Button>}
      />
      <div className="p-4 space-y-3" data-testid="audit-log-panel">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search summary, user, record #…" className="h-8 pl-7 w-64 text-xs" data-testid="audit-search" />
          </div>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="audit-filter-user"><SelectValue placeholder="All users" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Users</SelectItem>
              {facets.users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="audit-filter-category"><SelectValue placeholder="All activity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Activity</SelectItem>
              {facets.categories.map(c => <SelectItem key={c} value={c}>{c === "tax_override" ? "Tax overrides" : c === "request" ? "App changes" : c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="audit-filter-entity"><SelectValue placeholder="All records" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Records</SelectItem>
              {facets.entity_types.map(e => <SelectItem key={e} value={e}>{e.replace(/[-_]/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="audit-date-from" />
          <span className="text-xs text-slate-400">to</span>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="audit-date-to" />
        </div>

        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />
          <span>Tax overrides are logged with prior calculated rate/amount, the manual value and the confirmed tax no. Click a row to expand.</span>
        </div>

        {rows.length === 0 && !loading ? (
          <EmptyState title="Nothing logged yet" hint="Changes made in the app appear here as they happen." />
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Who</th>
                  <th className="px-3 py-2 font-medium">Area</th>
                  <th className="px-3 py-2 font-medium">What</th>
                </tr>
              </thead>
              <tbody>{rows.map(r => <AuditRow key={r.id} row={r} />)}</tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-slate-500">
          <span data-testid="audit-total">{total.toLocaleString()} entries</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)} data-testid="audit-prev">Prev</Button>
            <span>Page {page} / {pages}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= pages || loading} onClick={() => setPage(p => p + 1)} data-testid="audit-next">{tr("Next")}</Button>
          </div>
        </div>
      </div>
    </SettingsPanel>
  );
}
